import { Capacitor } from '@capacitor/core';

// ============================================================
// mobile-mode.ts
// ============================================================
// Scheduling-only access model — settled 2026-06-07, broadened 2026-06-20,
// corporate native carve-out added 2026-06-26. lead_manager briefly exempted
// (#875, 2026-07-31) then brought back under the native carve-out 2026-08-09.
// Carve-out extended from "native app" to "any phone (app OR phone browser)"
// 2026-08-21 (#1129) — a phone-browser login was slipping past the native-only
// check and handing leads/corporate the full app on their personal phone.
//
// employee + manager are confined to a scheduling-only surface on any
// PERSONAL login: the native iOS/Android app OR the web app on a personal
// device (role-based, not device-based).
//
// corporate AND lead_manager are confined on their PERSONAL PHONE — the native
// app OR the phone's web browser — and keep FULL access on the desktop/laptop
// work computer's web login. Rationale (CA off-the-clock-work protection): they
// do their operational work on a work computer (web login, unrestricted) and
// only reach the app from a personal phone; paring the personal-phone
// experience to scheduling keeps them from being pulled into off-clock work on
// their own device, and it can't be sidestepped by opening the site in the
// phone's browser instead of the app. They DO keep approving/denying requests
// on the phone — time-off, coverage and swap approvals live under the allowed
// /schedules prefix (SchedulesTimeOff / SchedulesRequests), gated by role
// inside those pages. admin / super_admin keep full access everywhere.
//
// (lead_manager history: #595 introduced the role copying corporate's
// carve-out; #875 exempted them after a Store Lead lost their nav on the
// native app; 2026-08-09 the exemption was walked back — leads get the same
// platform-split as corporate (full on web, scheduling + approve/deny on the
// phone), and clock-in moved kiosk-only for everyone, so the owned-clock nav
// juggling that motivated #875 no longer applies. Shift editing inside that
// scheduling surface is separate: #2166 lets a designated lead
// (can_edit_schedule + schedule.build) add, edit, and delete shifts on the
// phone. This file only decides which paths open.)
//
// The shared in-store KIOSK is the operational surface and is NEVER
// restricted here. The kiosk runs as a PIN session (loginMethod === 'pin'),
// which this gate explicitly excludes — so a manager/employee PIN session
// keeps its full operational nav. Pass loginMethod at every call site.
//
// NOTE: the boundary is ROLE + SESSION TYPE for employee/manager (same on
// native and web), but DEVICE for corporate AND lead_manager (any personal
// phone — native app or phone browser) — both flip with isPersonalPhone().
// admin/super_admin are unrestricted everywhere. Don't assume the gate is
// purely role-based.

const SCHEDULING_ONLY_PREFIXES: ReadonlyArray<string> = [
  '/dashboard',
  '/schedules',  // Broad prefix. Subroutes like /schedules/insights and
                 // /schedules/compliance are gated to admin+/corporate
                 // inside their page components — the gate is URL scope, not
                 // the role security boundary. Clock-in is the one carve-out
                 // (see SCHEDULING_ONLY_DENY below).
  // NOTE: /messages is intentionally absent. Free-form chat is kiosk-only
  // for employee/manager (CA off-the-clock posture, settled 2026-06-21):
  // they use the structured schedule forms (coverage / time-off / swap)
  // under /schedules, and receive manager announcements via push.
  '/account',
  '/help',
  '/auth',
  '/limited',
  '/kiosk',
  // Read-only status view for tickets the person submitted themselves. It has
  // to be reachable everywhere the feedback button is (the sidebar footer,
  // unaffected by this gate) — otherwise scheduling-only roles can file a
  // ticket but never find out what happened to it.
  '/my-tickets',
];

// Paths that stay BLOCKED for scheduling-only roles even though they fall
// under an allowed prefix. Clock-in is kiosk-only for employee/manager (CA
// off-the-clock posture + buddy-punch prevention): they punch at the shared
// in-store kiosk, never from a personal device.
const SCHEDULING_ONLY_DENY: ReadonlyArray<string> = [
  '/schedules/clock',
  '/timeclock',
  // Operational schedule subroutes: hidden from the scheduling-only nav (not
  // grayed) and blocked at the route level. NOTE /schedules/timesheets is the
  // TEAM-timecard surface — a lead_manager CAN view + adjust their store's
  // timecards there (#940), but only from the work-computer WEB login. On the
  // personal phone a lead is scheduling-only (#1061), so it stays denied here
  // like the rest of the operational surface (web-only confirmed by Brian
  // 2026-08-11 re: Lisa Cullins). compliance/insights are admin/corporate
  // analytics. Don't move these into an allow list to "fix" a lead's phone
  // access — that walks back the off-the-clock-work posture on purpose.
  '/schedules/timesheets',
  // Payroll is the same team-timecard page in export mode; same rule.
  '/schedules/payroll',
  '/schedules/compliance',
  '/schedules/insights',
];

export type Role =
  | 'employee'
  | 'manager'
  | 'admin'
  | 'corporate'
  | 'lead_manager'
  | 'super_admin'
  | undefined;

export type LoginMethod = 'email' | 'pin' | null | undefined;

export function isNativeMobile(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * True when running in a PHONE's web browser (Safari/Chrome on iOS/Android) —
 * i.e. mobile web, not the native app and not a desktop/laptop browser.
 *
 * The corporate/lead_manager scheduling-only gate was originally native-only
 * (isNativeMobile), which left a hole the size of the address bar: opening the
 * site in the phone's browser read as plain "web" and handed back the FULL app
 * (#1129 — Celia Martinez, a lead_manager, kept seeing sales + every ops page
 * on her phone). Brian 2026-08-21: the restriction follows the DEVICE, not the
 * shell — a personal phone is scheduling-only whether they use the installed
 * app OR the browser. Desktop/laptop web (the work computer) is deliberately
 * NOT matched, so leads/corporate keep the full app where their operational
 * work belongs.
 *
 * Phone-class user agents only. Tablets/iPad (modern iPadOS reports a desktop
 * UA anyway) fall through as desktop, matching "phone". A user can still force
 * "Request Desktop Site" to slip past UA sniffing; that closes the ordinary
 * case, which is the whole of the report.
 */
export function isPhoneWeb(): boolean {
  if (isNativeMobile()) return false; // native app is handled by isNativeMobile
  try {
    const ua =
      typeof navigator !== 'undefined' && navigator.userAgent
        ? navigator.userAgent
        : '';
    // Android phones carry "Mobile"; Android tablets don't — so the required
    // "Mobile" token keeps tablets out. iPhone/iPod are phones by definition.
    return /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(
      ua,
    );
  } catch {
    return false;
  }
}

/**
 * True for any PERSONAL phone context — the native iOS/Android app OR the
 * phone's web browser. This is the boundary for the corporate/lead_manager
 * scheduling-only carve-out: full app on the desktop/laptop work computer,
 * scheduling-only on the personal phone by either route.
 */
export function isPersonalPhone(): boolean {
  return isNativeMobile() || isPhoneWeb();
}

/**
 * True when the role must be confined to the scheduling-only surface.
 *
 *  - employee + manager: confined on ANY personal (email) login — native app
 *    OR personal web. Role-based, not device-based.
 *  - corporate + lead_manager: confined on their PERSONAL PHONE — the native
 *    app OR the phone's web browser (isPersonalPhone()). On the desktop/laptop
 *    work computer's WEB login they keep full access — that's where their
 *    operational work happens. CA off-the-clock-work protection. On the phone
 *    they still get the scheduling surface, which INCLUDES approving/denying
 *    time-off, coverage, and swap requests (those live under /schedules/* —
 *    see SchedulesTimeOff / SchedulesRequests).
 *    (lead_manager was previously exempt per #875; brought back under the
 *     off-the-clock carve-out per Brian 2026-08-09 — the scheduling surface
 *     plus approve/deny, not the rest of the app. Extended from native-only
 *     to any phone per Brian 2026-08-21, #1129 — a phone-browser login was
 *     bypassing the gate. #2166: a designated lead can edit shifts on that
 *     surface. This function does not decide edit rights.)
 *  - admin / super_admin: never confined, on any platform.
 *  - any kiosk PIN session (loginMethod === 'pin'): never confined — the
 *    shared in-store kiosk is the operational surface.
 */
export function isSchedulingOnly(role: Role, loginMethod?: LoginMethod): boolean {
  if (loginMethod === 'pin') return false; // kiosk = operational surface
  if (role === 'employee' || role === 'manager') return true;
  // corporate + lead_manager: full app on the desktop/laptop work computer; the
  // personal PHONE stays scheduling-only (off-the-clock posture) — whether they
  // use the native app OR the phone's web browser (#1129). Approve/deny of
  // requests still works there — those surfaces are under the allowed
  // /schedules prefix.
  if (role === 'corporate' || role === 'lead_manager') return isPersonalPhone();
  return false;
}

/**
 * True when an already-authenticated EMAIL session for a scheduling-only
 * role/platform should be bounced away from a kiosk-surface entry route
 * instead of reaching it.
 *
 * Every kiosk-surface entry point — `/limited`, `/kiosk` (+ its
 * `/c/:companyCode` variants), `/shared-login` (+ its variant) — is, by
 * design, unguarded: each has to work for a shared in-store device with no
 * email session at all. But each ALSO shows a self-serve "enter your PIN"
 * (or kiosk-unlock) card, and typing in a PIN there flips the session into
 * the full kiosk feature set (messaging, training, tasks, checklists, ...).
 * For a role that mobile-mode confines to scheduling-only on personal
 * login, that PIN pad is a complete bypass of the restriction from the very
 * same device/session it's supposed to apply to (#1129 — reported as "full
 * access as if she was on Kiosk mode"). #1130 closed this for `/limited`
 * alone; the ticket reopened because `/kiosk` has the identical PIN pad and
 * no guard at all, so this now gates every kiosk-surface route (see
 * KioskEntryGuard, the single call site all of them share).
 *
 * A genuine kiosk PIN session (loginMethod === 'pin') is never blocked
 * here — only the email-session escape hatch into it.
 */
export function blocksKioskEntryRoute(
  hasUserAndProfile: boolean,
  isPinSession: boolean,
  role: Role,
  loginMethod?: LoginMethod,
): boolean {
  if (!hasUserAndProfile || isPinSession) return false;
  return isSchedulingOnly(role, loginMethod);
}

/** Returns true if the given path is allowed for the role / session. */
export function isSchedulingPathAllowed(
  path: string,
  role: Role,
  loginMethod?: LoginMethod,
): boolean {
  if (!isSchedulingOnly(role, loginMethod)) return true;
  // Explicit deny wins over the broad allow prefixes (e.g. /schedules/clock
  // sits under the allowed /schedules prefix but stays kiosk-only).
  if (SCHEDULING_ONLY_DENY.some((p) => path === p || path.startsWith(p + '/'))) {
    return false;
  }
  return SCHEDULING_ONLY_PREFIXES.some(
    (prefix) =>
      path === prefix || path.startsWith(prefix + '/') || path.startsWith(prefix + '?'),
  );
}
